import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { changeProductStatus } from './statusesSlice';

// Типы для похожего товара
export interface SimilarProduct {
  product_id: string;
  product_name: string;
  product_url: string;
  product_thumb_url?: string;
  subject_id: number;
  subject_name: string;
  price_final_average: number;
  price_final_median: number;
  stat_revenue: number;
  stat_revenue_potential: number;
  stat_revenue_average: number;
  stat_lost_profit: number;
  stat_sales: number;
  stat_comments_count: number;
  stat_comments_valuation: number;
  stat_average_if_in_stock: number;
  stat_purchase_percent: number | string;
  seller_name: string;
  meta_country: string;
  meta_package_width?: number;
  meta_package_height?: number;
  meta_package_length?: number;
  status_id?: string;
  status_name?: string;
  status_is_positive?: boolean;
  similarity_score: number;
}

// Типы для состояния похожих товаров
interface SimilarProductsState {
  similarProducts: SimilarProduct[];
  loading: boolean;
  error: string | null;
  baseProductId: string | null;
  total: number;
  limit: number;
}

// Асинхронные действия
export const fetchSimilarProducts = createAsyncThunk(
  'similarProducts/fetchSimilarProducts',
  async ({ productId, limit = 20 }: { productId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await api.getSimilarProducts(productId, limit);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch similar products');
    }
  }
);

// Начальное состояние
const initialState: SimilarProductsState = {
  similarProducts: [],
  loading: false,
  error: null,
  baseProductId: null,
  total: 0,
  limit: 20,
};

// Слайс похожих товаров
const similarProductsSlice = createSlice({
  name: 'similarProducts',
  initialState,
  reducers: {
    clearSimilarProducts: (state) => {
      state.similarProducts = [];
      state.baseProductId = null;
      state.total = 0;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch similar products
      .addCase(fetchSimilarProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSimilarProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.similarProducts = action.payload.data || [];
        state.baseProductId = action.payload.base_product_id;
        state.total = action.payload.total || 0;
        state.limit = action.payload.limit || 20;
      })
      .addCase(fetchSimilarProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Обработка изменения статуса товара
      .addCase(changeProductStatus.fulfilled, (state, action) => {
        const { productId, statusId } = action.payload;
        const product = state.similarProducts.find(p => p.product_id === productId);
        if (product) {
          // Обновляем только ID статуса, название и is_positive получим при следующем обновлении товаров
          product.status_id = statusId;
          // Временно обновляем название статуса из других товаров с таким же статусом
          const statusProduct = state.similarProducts.find(p => p.status_id === statusId);
          if (statusProduct) {
            product.status_name = statusProduct.status_name;
            product.status_is_positive = statusProduct.status_is_positive;
          }
        }
      });
  },
});

export const { clearSimilarProducts, clearError } = similarProductsSlice.actions;
export default similarProductsSlice.reducer;
